import { useState } from 'react'
import { LuArrowRight, LuClock, LuCompass, LuOrbit, LuPalette, LuStar } from 'react-icons/lu'
import { appUrl } from '@astro/config'
import { ZODIAC_SIGNS } from './zodiacData'

export default function FreeHoroscopeSection() {
  const [selectedKey, setSelectedKey] = useState(ZODIAC_SIGNS[0].key)
  const reading = ZODIAC_SIGNS.find((sign) => sign.key === selectedKey) ?? ZODIAC_SIGNS[0]

  return (
    <section id="horoscope" className="relative overflow-hidden hero-vedic py-16 md:py-24 md:px-20 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] bg-[radial-linear(#E6C878_1px,transparent_1.5px)] bg-size-[26px_26px]" />
      <div className="pointer-events-none absolute -left-24 top-6 h-80 w-80 rounded-full bg-[radial-linear(circle,rgba(232,119,15,0.16),transparent_70%)] blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-[radial-linear(circle,rgba(122,31,43,0.30),transparent_70%)] blur-3xl" />

      <div className="container relative z-10 mx-auto px-6 max-w-350">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-secondary/15 text-secondary ring-1 ring-secondary/30">
                <LuStar className="h-4 w-4" />
              </span>
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.45em] text-secondary">Daily Predictions</span>
            </div>
            <h2 className="font-cinzel text-3xl leading-tight text-[#FBF3E4] md:text-4xl lg:text-5xl">
              Read Your <span className="text-linear-gold">Free Horoscope</span>
            </h2>
            <p className="max-w-xl text-sm leading-relaxed text-white/60 normal-case">
              Accurate daily, weekly and monthly insights for your career, love, health and finances — decoded from the planets moving through your Rashi, updated every day by Maharaj Ji.
            </p>

            <div className="grid grid-cols-2 gap-2.5">
              {[
                { label: 'Planetary Transits', Icon: LuOrbit },
                { label: 'Lucky Colour & Number', Icon: LuPalette },
                { label: 'Auspicious Muhurat', Icon: LuClock },
                { label: "Do's & Don'ts", Icon: LuCompass },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/4 px-3 py-2.5">
                  <item.Icon className="h-4 w-4 shrink-0 text-secondary" />
                  <span className="text-[11px] uppercase tracking-wide text-white/70">{item.label}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Pick your Rashi</p>
              <div className="grid grid-cols-6 gap-2 max-w-md">
                {ZODIAC_SIGNS.map((sign) => (
                  <button
                    key={sign.key}
                    type="button"
                    title={sign.name}
                    aria-pressed={sign.key === selectedKey}
                    onClick={() => setSelectedKey(sign.key)}
                    className={`grid h-10 w-full cursor-pointer place-items-center rounded-lg border text-2xl leading-none transition-all hover:-translate-y-0.5 ${
                      sign.key === selectedKey
                        ? 'border-secondary/60 bg-secondary/15 text-secondary'
                        : 'border-white/10 bg-white/4 text-[#F4DEA6] hover:border-secondary/50 hover:bg-secondary/10 hover:text-secondary'
                    }`}
                  >
                    {sign.glyph}
                  </button>
                ))}
              </div>
            </div>

            <a
              href={appUrl("website", "horoscope")}
              className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 active:scale-95 py-2 group h-12 rounded-full bg-secondary px-9 text-[11px] font-semibold uppercase tracking-[0.24em] text-primary shadow-lg shadow-secondary/20 transition-all hover:bg-[#FBF3E4] border-none"
            >
              See Horoscope
              <LuArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          <div>
            <div className="relative mx-auto w-full max-w-95 overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm shadow-2xl shadow-black/30">
              <span className="absolute inset-x-0 top-0 h-1 bg-linear-to-r from-secondary/0 via-secondary/70 to-secondary/0" />
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-linear-to-b from-[#F5DFA4] to-[#C99A2E] text-primary text-2xl shadow-[0_8px_22px_-8px_rgba(201,154,46,0.8)]">{reading.glyph}</span>
                <div className="min-w-0">
                  <h3 className="font-cinzel text-lg leading-tight text-[#FBF3E4]">{reading.name}</h3>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-secondary">Today&apos;s Reading</p>
                </div>
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-secondary/15 px-2.5 py-1 text-[11px] font-semibold text-secondary">
                  <LuStar className="h-3 w-3" fill="currentColor" />
                  {reading.rating}
                </span>
              </div>

              <div className="mt-5 space-y-3">
                {reading.stats.map((stat) => (
                  <div key={stat.label}>
                    <div className="mb-1 flex items-center justify-between text-[11px]">
                      <span className="inline-flex items-center gap-1.5 text-white/70">{stat.label}</span>
                      <span className="font-mono text-white/40">{stat.value}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-linear-to-r from-secondary to-[#E6C878]" style={{ width: `${stat.value}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 grid grid-cols-3 gap-2 border-t border-dashed border-white/15 pt-4 text-center">
                <div>
                  <p className="text-[8px] uppercase tracking-[0.2em] text-white/40">Colour</p>
                  <p className="mt-0.5 font-cinzel text-sm text-[#F4DEA6]">{reading.colour}</p>
                </div>
                <div>
                  <p className="text-[8px] uppercase tracking-[0.2em] text-white/40">Number</p>
                  <p className="mt-0.5 font-cinzel text-sm text-[#F4DEA6]">{reading.number}</p>
                </div>
                <div>
                  <p className="text-[8px] uppercase tracking-[0.2em] text-white/40">Time</p>
                  <p className="mt-0.5 font-cinzel text-sm text-[#F4DEA6]">{reading.time}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
